import "regenerator-runtime/runtime";
import React, { useMemo, useCallback } from "react";
import useForm from "./hooks/useForm";
import { useState, useDispatch } from "./hooks/useState";
import useCMS from "./hooks/useCMS";
import { usePost } from "./hooks/useApi";
import {
  insertInArray,
  isValidDate,
  pattern,
  parseStringAsDate,
} from "./utils";
import TypeOfReport from "./ui/TypeOfReport";
import DateInput from "./ui/DateInput";
import Select from "./ui/Select";
import CheckBox from "./ui/CheckBox";
import Button from "./ui/Button";
import Help from "./ui/Help";
import CustomerInfo from "./ui/CustomerInfo";
import TextInput from "./ui/TextInput";

const TAKEN_PART = "TakenPart";
const INFORM_EMPLOYER = "InformEmployer";
const FROM_DATE = "FromDate";
const ESTIMATED_RETURN_DATE = "EstimatedReturnDate";
const REPORT_TYPE = "ReportType";
const PHONE_ADVISING = "PhoneAdvising";
const MOBILE_NUMBER = "MobilePhone";
const REASON_OF_ABSENCE = "ReasonOfAbsence";
const WORK_RELATED_ABSENCE = "WorkRelatedAbsence";
const SICK = "Sick";
const VAB = "Vab";

function ReportAbsence() {
  const { translations, Sick } = useCMS();
  const {
    formType,
    hasVab,
    hasPhoneAdvising,
    hasAfterRegistration,
    customerInfo,
    hasVabPhoneAdvising,
    hasVabAfterRegistration,
    vabCustomerInfo,
    hasAbsence,
    lime,
    token,
  } = useState();
  const { setError, setSuccess } = useDispatch();
  const {
    handleSubmit,
    formState,
    watch,
    validateUntil,
    registerField,
    fieldRequired,
    trigger,
  } = useForm();

  const { post, isPending, isFulfilled } = usePost("SubmitSickReport", {
    onResolve: (res) => {
      const { ErrorCode } = res.data;
      if (ErrorCode) {
        setError(ErrorCode);
      } else {
        setSuccess("submitsuccess");
      }
    },
  });

  // show/hide inputs depending on type of report
  const watchTypeOfReport = watch(REPORT_TYPE, SICK);
  const isTypeOfReportSick = useMemo(
    () => watchTypeOfReport === SICK,
    [watchTypeOfReport]
  );

  /* const isPhoneAdvice = !!isTypeOfReportSick ? watch(PHONE_ADVISING, false) : undefined; */

  const watchWorkAbsence = isTypeOfReportSick
    ? watch(WORK_RELATED_ABSENCE, "")
    : undefined;
  const isAbsenceCausedByWork = useMemo(
    () => watchWorkAbsence === true,
    [watchWorkAbsence]
  );

  const watchReasons = isTypeOfReportSick
    ? watch(REASON_OF_ABSENCE, "")
    : undefined;

  const toggleWorkrelatedAbsence = useMemo(
    () =>
      watchReasons !== "commoncold" &&
      watchReasons !== "gastricflu" &&
      watchReasons !== "flu" &&
      watchReasons !== "",
    [watchReasons]
  );

  // assemble array with type of report alternatives
  const typeOfReportValues = useMemo(() => {
    const withAbsence = hasAbsence
      ? [{ value: SICK, label: translations.sicknessreport, checked: true }]
      : [];
    const withVab = hasVab ? [{ value: VAB, label: translations.vab }] : [];
    return [...withAbsence, ...withVab];
  }, [translations, hasVab]);

  // assemble an array with all the fields in the order they are displayed
  const fields = useMemo(() => {
    const baseFields = [REPORT_TYPE, TAKEN_PART];
    if (isTypeOfReportSick === undefined) return baseFields;
    const reportFields = isTypeOfReportSick
      ? [
          FROM_DATE,
          REASON_OF_ABSENCE,
          INFORM_EMPLOYER,
          WORK_RELATED_ABSENCE,
          ESTIMATED_RETURN_DATE,
          MOBILE_NUMBER,
        ]
      : [FROM_DATE, ESTIMATED_RETURN_DATE];
    const allFields = [...baseFields, ...reportFields];

    if (isTypeOfReportSick) {
      return hasPhoneAdvising
        ? insertInArray(allFields, 5, PHONE_ADVISING)
        : allFields;
    } else {
      return hasVabPhoneAdvising
        ? insertInArray(allFields, 3, PHONE_ADVISING)
        : allFields;
    }
  }, [isTypeOfReportSick, hasPhoneAdvising, hasVabPhoneAdvising]);

  const fromDateMin = useMemo(() => {
    if (isTypeOfReportSick) {
      return hasAfterRegistration ? null : new Date();
    } else {
      return hasVabAfterRegistration ? null : new Date();
    }
  }, [hasAfterRegistration, hasVabAfterRegistration, isTypeOfReportSick]);

  const watchFromDate = watch(FROM_DATE);
  const estimatedReturnMinDate = useMemo(() => {
    // return if FROM_DATE lacks date or time.
    if (!watchFromDate || !watchFromDate.split(" ")[1]) return null;
    const returnMinDate = parseStringAsDate(watchFromDate);
    // return if date is incorrect (i.e if time is chosen first there is a X in date)
    if (!isValidDate(returnMinDate)) return null;

    returnMinDate.setMinutes(returnMinDate.getMinutes() + 1);
    return returnMinDate;
  }, [watchFromDate]);

  // trigger validate until the field that has focus
  const onFieldFocus = useCallback(
    (event) => validateUntil(fields, event.target.name),
    [fields]
  );

  const onSubmit = useCallback(
    (data) => {
      const extraField = data[REASON_OF_ABSENCE]
        ? {}
        : { ReasonOfAbsence: watchTypeOfReport };
      const postData = {
        ...data,
        ...lime,
        ...extraField,
        WorkRelatedAbsence: isAbsenceCausedByWork,
        InformEmployer: !data[INFORM_EMPLOYER],
        Token: token,
      };
      post(postData);
    },
    [post, lime, token, formType]
  );

  const isLoading = formState.isSubmitting || isPending || isFulfilled;
  const togglePhoneFields =
    (isTypeOfReportSick && hasPhoneAdvising) ||
    (!isTypeOfReportSick && hasVabPhoneAdvising);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TypeOfReport
        {...registerField(REPORT_TYPE)}
        values={typeOfReportValues}
        onFocus={onFieldFocus}
      >
        {translations.typeofreport}
      </TypeOfReport>

      {isTypeOfReportSick && !!customerInfo && (
        <CustomerInfo
          {...registerField(TAKEN_PART)}
          heading={Sick.messageFromEmployerHeadline}
          label={translations.takenPart}
        >
          {isTypeOfReportSick ? customerInfo : vabCustomerInfo}
        </CustomerInfo>
      )}

      {!isTypeOfReportSick && !!vabCustomerInfo && (
        <CustomerInfo
          {...registerField(TAKEN_PART)}
          heading={Sick.messageFromEmployerHeadline}
          label={translations.takenPart}
        >
          {isTypeOfReportSick ? customerInfo : vabCustomerInfo}
        </CustomerInfo>
      )}

      <DateInput
        {...registerField(FROM_DATE, true)}
        minDate={fromDateMin}
        required={fieldRequired}
        onFocus={onFieldFocus}
        pattern={pattern.date}
      >
        {translations.fromdateandtime}
      </DateInput>

      {isTypeOfReportSick ? (
        <Select
          {...registerField(REASON_OF_ABSENCE)}
          defaultValue={translations.choosecause}
          options={Sick.reasons}
          required={fieldRequired}
          onFocus={onFieldFocus}
        >
          {translations.cause}
        </Select>
      ) : null}

      {togglePhoneFields ? (
        <CheckBox {...registerField(PHONE_ADVISING)} onFocus={onFieldFocus}>
          {translations.iwanttobecalled}
        </CheckBox>
      ) : null}

      {isTypeOfReportSick && toggleWorkrelatedAbsence ? (
        <>
          <p>{Sick.workRelatedText}</p>
          <CheckBox
            {...registerField(WORK_RELATED_ABSENCE)}
            onFocus={onFieldFocus}
          >
            {Sick.workRelatedAbsence}
          </CheckBox>
          {isAbsenceCausedByWork && <Help>{Sick.absenceCausedByWorkText}</Help>}
        </>
      ) : null}

      <DateInput
        {...registerField(ESTIMATED_RETURN_DATE, true)}
        required={fieldRequired}
        pattern={pattern.date}
        onFocus={onFieldFocus}
        minDate={estimatedReturnMinDate}
      >
        {translations.preliminaryReturnDateLabel}
      </DateInput>

      <Help>{Sick.returnDateLabel}</Help>

      {isTypeOfReportSick ? (
        <div className="field">
          <label className="label" htmlFor={INFORM_EMPLOYER}>
            {translations.secrecy}
          </label>

          <CheckBox {...registerField(INFORM_EMPLOYER)} onFocus={onFieldFocus}>
            {translations.informemployer}
          </CheckBox>
        </div>
      ) : null}

      <h4>{translations.contactinformation}</h4>
      <TextInput
        {...registerField(MOBILE_NUMBER)}
        onFocus={onFieldFocus}
        required={fieldRequired}
        type="tel"
        pattern={pattern.mobileNumber}
        onChange={async () => {
          const isValid = await trigger(MOBILE_NUMBER);
          return isValid;
        }}
      >
        {translations.mobilephone}
      </TextInput>

      <Button
        type="submit"
        loading={isLoading}
        disabled={!formState.isValid || isLoading}
      >
        {translations.send}
      </Button>
    </form>
  );
}

export default ReportAbsence;
